import React from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BodyText } from '../bodyText/BodyText'
import { LoadingSkeleton } from '../loadingSkeleton/LoadingSkeleton'
import { Title } from '../title/Title'
import { Tooltip, TOOLTIP_TRIGGER_ICON } from '../tooltip/Tooltip'

export interface InsightsMetricCardProps {
  title: string
  value?: string
  goal?: string
  programAverage?: string
  tooltipContent?: string
  testID?: string
  isLoading?: boolean
  metricsPerRow?: number
  showBorder?: boolean
}

const getFlexBasisWithGap = (metricsPerRow: number, gapPx: number): string => {
  const percentage = 100 / metricsPerRow
  const gapsPerRow = metricsPerRow - 1
  const gapPerItem = (gapsPerRow * gapPx) / metricsPerRow
  return `calc(${percentage}% - ${gapPerItem}px)`
}

const CardContainer = styled.View<{
  metricsPerRow: number
  showBorder?: boolean
}>(({ theme, metricsPerRow, showBorder }) => ({
  flexDirection: 'column',
  gap: theme.spacing['12px'],
  padding: theme.spacing['16px'],
  borderRadius: '8px',
  borderWidth: showBorder ? '1px' : 0,
  borderColor: theme.colors.borderDefault,
  backgroundColor: theme.colors.backgroundElement,
  flexBasis: getFlexBasisWithGap(metricsPerRow, 16),
  flexShrink: 0,
  flexGrow: 0,
}))

const DataRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const TitleContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
})

export const InsightsMetricCard: React.FC<InsightsMetricCardProps> = ({
  title,
  value,
  goal,
  programAverage,
  tooltipContent,
  testID = 'InsightsMetricCard',
  isLoading = false,
  metricsPerRow = 3,
  showBorder,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  return (
    <CardContainer testID={tID(testID)} metricsPerRow={metricsPerRow} showBorder={showBorder}>
      <DataRow>
        <TitleContainer>
          <Title text={title} testID={`${testID}-title`} size={TitleSize.XSMALL} />
          {tooltipContent && (
            <Tooltip
              testID={testID}
              content={tooltipContent}
              accessibilityLabel={tooltipContent}
              triggerConfig={{
                icon: TOOLTIP_TRIGGER_ICON.INFO,
                size: 24,
                fillColor: colors.textPrimary,
              }}
              placement='top'
              hoverEnabled
            />
          )}
        </TitleContainer>
        {isLoading ? (
          <LoadingSkeleton
            width={30}
            maxWidth='50%'
            height={16}
            marginTop={4}
            testID={`${testID}-valueLoadingSkeleton`}
          />
        ) : (
          <BodyText
            text={
              value ??
              formatMessage({
                defaultMessage: 'N/A',
                description: 'value is not available in Insights metric card',
              })
            }
            testID={`${testID}-value`}
            size={BodyTextSize.DEFAULT}
          />
        )}
      </DataRow>
      {goal && (
        <DataRow>
          <BodyText
            text={formatMessage({
              defaultMessage: 'Goal: ',
              description: 'Label for goal in Insights metric card',
            })}
            testID={`${testID}-goalLabel`}
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
          />
          <BodyText text={goal} testID={`${testID}-goalValue`} size={BodyTextSize.SMALL} color={colors.textSecondary} />
        </DataRow>
      )}
      {programAverage && (
        <DataRow>
          <BodyText
            text={formatMessage({
              defaultMessage: 'Program average: ',
              description: 'Label for program average in Insights metric card',
            })}
            testID={`${testID}-programAverageLabel`}
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
          />
          {isLoading ? (
            <LoadingSkeleton
              width={30}
              maxWidth='50%'
              height={16}
              marginTop={4}
              testID={`${testID}-programAverageLoadingSkeleton`}
            />
          ) : (
            <BodyText
              text={programAverage}
              testID={`${testID}-programAverageValue`}
              size={BodyTextSize.SMALL}
              color={colors.textSecondary}
            />
          )}
        </DataRow>
      )}
    </CardContainer>
  )
}

export default InsightsMetricCard
